import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8008/api";
  }
  return "/api";
};

export const communityApi = createApi({
  reducerPath: "communityApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache");
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: [
    "CommunityFeed",
    "CommunityRoster",
    "CommunityMatchup",
    "CommunityChallenge",
    "CommunityDebate",
    "CommunityProfile",
    "CommunityDaily",
  ],
  endpoints: (builder) => ({
    getCommunityLineup: builder.query({
      query: (sport = "mlb") =>
        `/community/lineup?sport=${encodeURIComponent(sport)}`,
      transformResponse: (response) => response.roster,
      providesTags: (_result, _error, sport) => [
        { type: "CommunityRoster", id: `lineup-${sport}` },
      ],
    }),
    getCommunityFeed: builder.query({
      query: (sport = "mlb") => `/community/feed?sport=${encodeURIComponent(sport)}`,
      providesTags: ["CommunityFeed"],
    }),
    searchCommunity: builder.query({
      query: ({ sport = "mlb", q }) =>
        `/community/search?sport=${encodeURIComponent(sport)}&q=${encodeURIComponent(
          q || ""
        )}`,
      providesTags: ["CommunityFeed"],
    }),
    getCommunityRoster: builder.query({
      query: (idOrSlug) => `/community/rosters/${encodeURIComponent(idOrSlug)}`,
      transformResponse: (response) => response.roster,
      providesTags: (_result, _error, id) => [{ type: "CommunityRoster", id }],
    }),
    getCommunityMatchup: builder.query({
      query: (id) => `/community/matchups/${encodeURIComponent(id)}`,
      transformResponse: (response) => response.matchup,
      providesTags: (_result, _error, id) => [{ type: "CommunityMatchup", id }],
    }),
    getCommunityChallenge: builder.query({
      query: (idOrSlug) => `/community/challenges/${encodeURIComponent(idOrSlug)}`,
      transformResponse: (response) => ({
        ...response.challenge,
        entries: response.entries || [],
      }),
      providesTags: (_result, _error, id) => [{ type: "CommunityChallenge", id }],
    }),
    getCommunityDebate: builder.query({
      query: (idOrSlug) => `/community/debates/${encodeURIComponent(idOrSlug)}`,
      transformResponse: (response) => response.debate,
      providesTags: (_result, _error, id) => [{ type: "CommunityDebate", id }],
    }),
    getCommunityDaily: builder.query({
      query: (sport) => `/community/daily?sport=${encodeURIComponent(sport)}`,
      transformResponse: (response) => response.debate,
      providesTags: (_result, _error, sport) => [
        { type: "CommunityDaily", id: sport },
        "CommunityDaily",
      ],
    }),
    getCommunityProfile: builder.query({
      query: ({ handle, sport }) =>
        `/community/profiles/${encodeURIComponent(handle)}${
          sport ? `?sport=${encodeURIComponent(sport)}` : ""
        }`,
      transformResponse: (response) => response.profile,
      providesTags: (_result, _error, arg) => [
        { type: "CommunityProfile", id: arg.handle },
      ],
    }),
    createCommunityChallenge: builder.mutation({
      query: (body) => ({ url: "/community/challenges", method: "POST", body }),
      transformResponse: (response) => response.challenge,
      invalidatesTags: ["CommunityFeed", "CommunityChallenge"],
    }),
    createCommunityDebate: builder.mutation({
      query: (body) => ({ url: "/community/debates", method: "POST", body }),
      transformResponse: (response) => response.debate,
      invalidatesTags: ["CommunityFeed", "CommunityDebate"],
    }),
    voteCommunityDebate: builder.mutation({
      query: ({ id, choiceId }) => ({
        url: `/community/debates/${encodeURIComponent(id)}/vote`,
        method: "POST",
        body: { choiceId },
      }),
      transformResponse: (response) => response.debate,
      invalidatesTags: (_result, _error, arg) => [
        { type: "CommunityDebate", id: arg.id },
        "CommunityFeed",
        { type: "CommunityDaily" },
      ],
    }),
    commentCommunityDebate: builder.mutation({
      query: ({ id, body, parentId }) => ({
        url: `/community/debates/${encodeURIComponent(id)}/comments`,
        method: "POST",
        body: { body, parentId },
      }),
      transformResponse: (response) => response.debate,
      invalidatesTags: (_result, _error, arg) => [
        { type: "CommunityDebate", id: arg.id },
        "CommunityFeed",
        "CommunityDaily",
      ],
    }),
    getAdminCommunityDaily: builder.query({
      query: ({ sport, date }) =>
        `/community/admin/daily?sport=${encodeURIComponent(sport)}${
          date ? `&date=${encodeURIComponent(date)}` : ""
        }`,
      providesTags: ["CommunityDaily"],
    }),
    pinAdminCommunityDaily: builder.mutation({
      query: (body) => ({ url: "/community/admin/daily", method: "PUT", body }),
      invalidatesTags: ["CommunityDaily", "CommunityFeed"],
    }),
    previewAdminCommunityDaily: builder.mutation({
      query: (body) => ({
        url: "/community/admin/daily/preview",
        method: "POST",
        body,
      }),
    }),
    regenerateAdminCommunityDaily: builder.mutation({
      query: (body) => ({
        url: "/community/admin/daily/regenerate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CommunityDaily", "CommunityFeed"],
    }),
    getAdminCommunityHistory: builder.query({
      query: (sport) =>
        `/community/admin/daily/history?sport=${encodeURIComponent(sport)}`,
      providesTags: ["CommunityDaily"],
    }),
    deleteAdminCommunityComment: builder.mutation({
      query: (id) => ({
        url: `/community/admin/comments/${encodeURIComponent(id)}/delete`,
        method: "POST",
      }),
      invalidatesTags: ["CommunityDaily", "CommunityDebate"],
    }),
    archiveAdminPromptKey: builder.mutation({
      query: (body) => ({
        url: "/community/admin/prompt-keys/archive",
        method: "POST",
        body,
      }),
    }),
    createCommunityRoster: builder.mutation({
      query: (body) => ({ url: "/community/rosters", method: "POST", body }),
      transformResponse: (response) => response.roster,
      invalidatesTags: ["CommunityFeed"],
    }),
    updateCommunityRoster: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/community/rosters/${encodeURIComponent(id)}`,
        method: "PUT",
        body,
      }),
      transformResponse: (response) => response.roster,
      invalidatesTags: (_result, _error, arg) => [
        { type: "CommunityRoster", id: arg.id },
        "CommunityFeed",
      ],
    }),
    publishCommunityRoster: builder.mutation({
      query: (id) => ({
        url: `/community/rosters/${encodeURIComponent(id)}/publish`,
        method: "POST",
      }),
      invalidatesTags: ["CommunityFeed", "CommunityRoster", "CommunityMatchup"],
    }),
    remixCommunityRoster: builder.mutation({
      query: (id) => ({
        url: `/community/rosters/${encodeURIComponent(id)}/remix`,
        method: "POST",
      }),
      transformResponse: (response) => response.roster,
      invalidatesTags: ["CommunityFeed"],
    }),
    challengeCommunityRoster: builder.mutation({
      query: ({ id, title }) => ({
        url: `/community/rosters/${encodeURIComponent(id)}/challenge`,
        method: "POST",
        body: { title },
      }),
      invalidatesTags: ["CommunityFeed"],
    }),
    enterCommunityChallenge: builder.mutation({
      query: ({ id, title }) => ({
        url: `/community/challenges/${encodeURIComponent(id)}/enter`,
        method: "POST",
        body: { title },
      }),
      invalidatesTags: ["CommunityFeed", "CommunityChallenge"],
    }),
    voteCommunityMatchup: builder.mutation({
      query: ({ id, choiceId }) => ({
        url: `/community/matchups/${encodeURIComponent(id)}/vote`,
        method: "POST",
        body: { choiceId },
      }),
      transformResponse: (response) => response.matchup,
      invalidatesTags: (_result, _error, arg) => [
        { type: "CommunityMatchup", id: arg.id },
        "CommunityFeed",
      ],
    }),
  }),
});

export const {
  useGetCommunityLineupQuery,
  useGetCommunityFeedQuery,
  useSearchCommunityQuery,
  useGetCommunityRosterQuery,
  useGetCommunityMatchupQuery,
  useGetCommunityChallengeQuery,
  useGetCommunityDebateQuery,
  useGetCommunityDailyQuery,
  useGetCommunityProfileQuery,
  useCreateCommunityRosterMutation,
  useUpdateCommunityRosterMutation,
  usePublishCommunityRosterMutation,
  useRemixCommunityRosterMutation,
  useChallengeCommunityRosterMutation,
  useEnterCommunityChallengeMutation,
  useCreateCommunityChallengeMutation,
  useCreateCommunityDebateMutation,
  useVoteCommunityMatchupMutation,
  useVoteCommunityDebateMutation,
  useCommentCommunityDebateMutation,
  useGetAdminCommunityDailyQuery,
  usePinAdminCommunityDailyMutation,
  usePreviewAdminCommunityDailyMutation,
  useRegenerateAdminCommunityDailyMutation,
  useGetAdminCommunityHistoryQuery,
  useDeleteAdminCommunityCommentMutation,
  useArchiveAdminPromptKeyMutation,
} = communityApi;
